/**
 * Create New Record Binding
 * Utility for creating data bindings for new records that don't yet exist in the repository
 */

import type { ShapeDataBinding, LocalChanges } from '../models/ShapeDataBinding';
import { toISODateString } from '../models/ShapeDataBinding';

/**
 * Generate a temporary record ID for new records
 */
export function generateTemporaryRecordId(shapeId: string): string {
  return `NEW-${shapeId}`;
}

/**
 * Check if a record ID is a temporary ID (for new records)
 */
export function isTemporaryRecordId(recordId: string): boolean {
  return recordId.startsWith('NEW-');
}

/**
 * Options for creating a new record binding
 */
export interface CreateNewRecordBindingOptions {
  shapeId: string;
  sourceId: string;
  userId: string;
  initialData?: Record<string, unknown>;
}

/**
 * Create a data binding for a new record that doesn't exist in the repository yet
 *
 * This is used when:
 * 1. User creates a new shape in a repository-connected diagram
 * 2. User drags from shape library in a repo-connected diagram
 * 3. User wants to add a new process to the repository from the diagram
 *
 * The binding will have:
 * - `isNewRecord: true` to indicate it needs to be created in the repository
 * - A temporary `recordId` in the format `NEW-{shapeId}`
 * - Empty `cachedRecord` since nothing exists in the repository
 * - Initial data stored in `localChanges`
 *
 * On commit, the service will:
 * 1. Create the record in the external repository
 * 2. Update `recordId` with the real ID from the repository
 * 3. Set `cachedRecord` with the committed data
 * 4. Clear `localChanges` and `isNewRecord`
 */
export function createNewRecordBinding(
  options: CreateNewRecordBindingOptions
): ShapeDataBinding {
  const { shapeId, sourceId, userId, initialData = {} } = options;

  const now = toISODateString(new Date());
  const tempRecordId = generateTemporaryRecordId(shapeId);

  // Create local changes with any initial data
  const localChanges: LocalChanges = {
    fields: initialData,
    changedAt: now,
    changedBy: userId,
  };

  return {
    sourceId,
    recordId: tempRecordId,
    cachedRecord: {}, // Empty - doesn't exist in repository yet
    syncState: { status: 'synced', syncedAt: now },
    localChanges,
    isNewRecord: true,
    localVersion: 1,
    lastWrittenVersion: 0,
  };
}

/**
 * Check if a binding is for a new record (not yet in repository)
 */
export function isNewRecordBinding(binding: ShapeDataBinding): boolean {
  return binding.isNewRecord === true || isTemporaryRecordId(binding.recordId);
}

/**
 * Get the display name for a new record binding
 * Falls back to a default name if no name is in the initial data
 */
export function getNewRecordDisplayName(
  binding: ShapeDataBinding,
  nameField: string = 'name',
  defaultName: string = 'New Process'
): string {
  // First check local changes (uncommitted data)
  if (binding.localChanges?.fields[nameField]) {
    return String(binding.localChanges.fields[nameField]);
  }

  // Then check cached record (shouldn't have anything for new records)
  if (binding.cachedRecord[nameField]) {
    return String(binding.cachedRecord[nameField]);
  }

  return defaultName;
}

/**
 * Prepare a new record for commit to the repository
 * This merges the cached record with local changes to get the final data
 */
export function prepareNewRecordForCommit(
  binding: ShapeDataBinding
): Record<string, unknown> {
  // For new records, we only have local changes (no cached data)
  return {
    ...binding.cachedRecord,
    ...binding.localChanges?.fields,
  };
}

/**
 * Update a binding after successfully creating the record in the repository
 * This is called after commit succeeds for new records
 */
export function finalizeNewRecordBinding(
  binding: ShapeDataBinding,
  permanentRecordId: string,
  committedData: Record<string, unknown>
): ShapeDataBinding {
  return {
    ...binding,
    recordId: permanentRecordId,
    cachedRecord: committedData,
    syncState: { status: 'synced', syncedAt: toISODateString(new Date()) },
    localChanges: undefined,
    isNewRecord: false,
    lastWrittenVersion: binding.localVersion,
  };
}
